//we need this library to make this code work inside web worker
import * as cheerio from "cheerio";

import { viteEnv } from "utils/vite-env.util";

import {
  BLANK_FIELD_ATTRIBUTE,
  BLANK_LENGTH_ATTRIBUTE,
  BLANK_PAD_ATTRIBUTE,
  BLANK_TEXT_ATTRIBUTE,
  BOX_ATTRIBUTE,
  BOX_LINE_ATTRIBUTE,
  BOX_LINE_STYLE,
  CHECKBOX_PRINT_GLYPHS,
  CellPosition,
  FORM_CELL_PADDING_PX,
  FORM_LINE_HEIGHT,
  FORM_PAGE_DPI,
  FormSettings,
  LEGACY_PAGE_DPI,
  SYMBOL_PRINT_GLYPHS,
  TABLE_LINES_ATTRIBUTE,
  TableLinesMode,
  cellBorderDeclarations,
  formContentWidthPx,
  getCellBorders,
  pointLengthsToPixels,
  readBoxSize,
  readTableLinesMode
} from "./form-print.utils";
import { FORM_FONT_FAMILIES, mapSourceFontFamily } from "./pdf-fonts";
import { getImageSize } from "./utils";

const SIGN_BUTTON_PLACEHOLDER = `<button class="sign-button" data-id="sign-button">Click to Add Signature</button>`;

const SALE_PLACEHOLDER_NAMES = [
  "Patient.SelectedInvoice.LineItems",
  "Patient.SelectedInvoice.Devices",
  "Patient.SelectedInvoice.Services",
  "Patient.SelectedInvoice.Totals"
] as const;

const getSalePlaceholderHtml = (mergeTagName: string) => {
  const selector = mergeTagName.replaceAll(" ", "-").replaceAll(".", "-");
  return `<button class="select-invoice-button ${selector}" data-id="select-invoice-button-.${selector}" data-options="">Click to Select Invoice</button>`;
};

const formatResourceUrlLocal = (url?: string, bustCache: boolean = false) => {
  if (!url || url.startsWith("blob:") || url.startsWith("data:")) return url;

  let formattedUrl: string;
  if (url.startsWith("/")) {
    formattedUrl = (typeof viteEnv !== "undefined" ? viteEnv.VITE_SUNO_API : "")
      ?.replace("/api", "")
      .concat(url);
  } else {
    formattedUrl = url;
  }

  if (bustCache && formattedUrl) {
    const cacheParam = `${formattedUrl.includes("?") ? "&" : "?"}_cb=`;
    if (!formattedUrl.includes(cacheParam)) {
      formattedUrl += `${cacheParam}${Date.now()}`;
    }
  }

  return formattedUrl;
};

const IMAGE_DOWN_RATIO = 0.8;
// A Form prints at 96 dpi (FORM_PAGE_DPI), so an inch is 96 of its px.
const CSS_PX_PER_INCH = 96;
const IMAGE_MAX_WIDTH = 1000;
// A4 page width (595pt) minus paddingHorizontal 50pt each side = 495pt usable content width.
// react-pdf treats CSS px as pt, so px values map 1:1 to pt in the rendered PDF.
const PDF_PAGE_CONTENT_WIDTH = 495;

type ImageLimits = {
  // Wider images are scaled down to this width, keeping their aspect ratio.
  contentWidth: number;
  // The max-width given to an image with no usable size.
  maxWidth: number;
  // A Form image carries its printed size in inches (form-image.utils); other templates size in px.
  sizedInAnyUnit?: boolean;
  // Multiplies the fixed px size of a signature outside a box. Those sizes were chosen for a page
  // at LEGACY_PAGE_DPI; a page at another dpi scales them to print the same physical size.
  signatureScale?: number;
};

const LEGACY_IMAGE_LIMITS: ImageLimits = {
  contentWidth: PDF_PAGE_CONTENT_WIDTH,
  maxWidth: IMAGE_MAX_WIDTH
};

// ENG-2277: a Form's page is its own size, so its images fit between its own margins.
const formImageLimits = (formSettings: FormSettings): ImageLimits => {
  const contentWidth = formContentWidthPx(formSettings);
  return {
    contentWidth,
    maxWidth: contentWidth,
    sizedInAnyUnit: true,
    signatureScale: FORM_PAGE_DPI / LEGACY_PAGE_DPI
  };
};

const SPECIAL_CHARS = new Set([
  "ʃ",
  "ʒ",
  "θ",
  "ð",
  "ŋ",
  "ɛ",
  "æ",
  "ʊ",
  "ɔ",
  "Ʃ",
  "Ʒ",
  "Θ",
  "Ð",
  "Ŋ",
  "Ɛ",
  "Æ",
  "Ʊ",
  "Ɔ"
]);

// Unicode characters that should be normalized to ASCII equivalents for PDF rendering
// The Lato font doesn't include glyphs for many of these Unicode variants
const CHAR_NORMALIZATION_MAP: Record<string, string> = {
  // Hyphens and dashes → ASCII hyphen-minus (U+002D)
  "\u2010": "-", // Hyphen
  "\u2011": "-", // Non-breaking Hyphen
  "\u2012": "-", // Figure Dash
  "\u2013": "-", // En Dash
  "\u2014": "-", // Em Dash
  "\u2015": "-", // Horizontal Bar
  "\u2212": "-", // Minus Sign
  "\uFE58": "-", // Small Em Dash
  "\uFE63": "-", // Small Hyphen-Minus
  "\uFF0D": "-", // Fullwidth Hyphen-Minus
  // Slashes → ASCII forward slash (U+002F)
  "\u2044": "/", // Fraction Slash
  "\u2215": "/", // Division Slash
  "\uFF0F": "/", // Fullwidth Solidus
  // Quotes → ASCII equivalents
  "\u2018": "'", // Left Single Quotation Mark
  "\u2019": "'", // Right Single Quotation Mark
  "\u201A": "'", // Single Low-9 Quotation Mark
  "\u201B": "'", // Single High-Reversed-9 Quotation Mark
  "\u2032": "'", // Prime
  "\u201C": '"', // Left Double Quotation Mark
  "\u201D": '"', // Right Double Quotation Mark
  "\u201E": '"', // Double Low-9 Quotation Mark
  "\u201F": '"', // Double High-Reversed-9 Quotation Mark
  "\u2033": '"', // Double Prime
  // Spaces → ASCII space
  "\u00A0": " ", // Non-breaking Space
  "\u2002": " ", // En Space
  "\u2003": " ", // Em Space
  "\u2009": " ", // Thin Space
  "\u200A": " " // Hair Space
};

// Pre-compile regex for character normalization
const CHAR_NORMALIZATION_REGEX = new RegExp(
  `[${Object.keys(CHAR_NORMALIZATION_MAP).join("")}]`,
  "g"
);

// Normalize Unicode characters to ASCII equivalents for PDF font compatibility
const normalizeCharactersForPdf = (text: string): string => {
  return text.replace(
    CHAR_NORMALIZATION_REGEX,
    (char) => CHAR_NORMALIZATION_MAP[char] || char
  );
};

const CHECKED_IMAGE_DATA =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAMAAABEpIrGAAAA8FBMVEUAAABAm/8+lf9Blv8/mP9Bmf9Amf9Amf9Amf8/mf9Amf9Amf8/mf9Amf9Amf9Bmf9Bmv9Cmv9Dm/9FnP9Hnf9Knv9Ln/9NoP9Pof9UpP9VpP9Ypv9Zpv9lrf9mrf9or/9rsP9xs/9ztP90tf93tv94t/98uf99uf99uv+Evf+Hv/+Pw/+QxP+Uxv+Xx/+YyP+gzP+hzf+w1f+x1f+y1v+z1v+42f+52v/C3//F4P/G4f/J4v/L4//N5P/S5//Y6v/b7P/d7f/k8f/n8v/p8//r9P/s9f/u9v/y+P/3+//4+//5/P/6/P/7/f/9/v////9mUtzUAAAADnRSTlMAHB0nqaqsr+zt7vDx8r0xoZ0AAAABYktHRE9uZkFJAAAA8UlEQVQ4y7WTyVICQRBEi2FTZEpQRgVF3HDfd0VFcWFR+/3/33jAMAKcrjmRx+oX0d2ZWSJBLlSPwnxaJJhWQ4VAcmoqK6ENFEUTNDGg8rBjA9dwExnAIdCt+oHGF7hN/xWLr8CJ/w2lFnA3OwrUbqM/4AzoLIx+s9ahNfc72XDwWR/z4Qk4Hw6We8D+uFH1PnCkqjrfBi7+O9l04LZV9Qp4rsRYfQwMGroH9FZis7gEPpoDcFvxYZXvGerUl2b0AsBj2Rt39R14WzL6sNrne80szK47SGjU+kRKm7A4M5K3gYykC9b5VEokyBa9i5lJyQ+0alHb7i7hVgAAAABJRU5ErkJggg==";

const NON_CHECKED_IMAGE_DATA =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAwAAAAMCAYAAABWdVznAAAACXBIWXMAAA7EAAAOxAGVKw4bAAAAVklEQVQoke3OoRHAMAwDQCnnMg9l7vB6w6zhocISUNReUZMBKvzSie4eJBv2EkKy9d6PzJxf0sxEVUcBgBV+m7J55clf2C6YmazgbQRAqOqotS7XSZ4XcaAVtq2QwAkAAAAASUVORK5CYII=";

// Pre-compile regex for better performance
const SPECIAL_CHARS_REGEX = new RegExp(
  `(${[...SPECIAL_CHARS].join("|")})`,
  "g"
);

const removePlaceholders = (html: string) => {
  if (html.includes(SIGN_BUTTON_PLACEHOLDER)) {
    html = html.replaceAll(SIGN_BUTTON_PLACEHOLDER, "");
  }
  for (const name of SALE_PLACEHOLDER_NAMES) {
    const placeholder = getSalePlaceholderHtml(name);
    if (html.includes(placeholder)) {
      html = html.replaceAll(placeholder, "");
    }
  }
  if (html.includes(`<button class="select-invoice-button`)) {
    const $ = cheerio.load(html);
    $(`button.select-invoice-button`).remove();
    html = $.html();
  }
  return html;
};

type NormalizePrintableHtmlOptions = {
  enableTableLayoutFixes?: boolean;
  materializeRowspans?: boolean;
  stripRowspanCellHeights?: boolean;
  preserveBlankLines?: boolean;
  // ENG-2277: present only for a Form. Absent (or null), output is unchanged.
  formSettings?: FormSettings | null;
};

// Simple cache for normalized HTML to avoid recomputing identical inputs
const normalizeCache = new Map<string, string>();
const MAX_CACHE_SIZE = 100; // Limit cache size to prevent memory issues
// An entry holds its whole input (as the key) and its whole output. A Form's images are data URIs
// inside the HTML, so one body can run to megabytes and a full cache to hundreds of them; a Form
// whose key is longer than this is normalized every time instead. Every other document is kept
// whatever its size, as it always has been.
export const NORMALIZE_CACHE_MAX_KEY_LENGTH = 250000;

export const normalizePrintableHtml = (
  data: string,
  options: NormalizePrintableHtmlOptions = {}
) => {
  const { formSettings } = options;
  const formKey = formSettings ? JSON.stringify(formSettings) : "page";
  const cacheKey = `${options.enableTableLayoutFixes ? "layout" : "legacy"}:${options.materializeRowspans ? "materialize" : "preserve-spans"}:${options.stripRowspanCellHeights ? "strip" : "preserve"}:${options.preserveBlankLines ? "blank-lines" : "no-blank-lines"}:${formKey}:${data}`;
  const cacheable =
    !formSettings || cacheKey.length <= NORMALIZE_CACHE_MAX_KEY_LENGTH;

  // Check cache first
  if (cacheable && normalizeCache.has(cacheKey)) {
    return normalizeCache.get(cacheKey)!;
  }

  // Normalize Unicode characters to ASCII equivalents for PDF font compatibility
  // This must be done before cheerio parsing to ensure proper character handling
  const normalizedData = normalizeCharactersForPdf(data);
  const html = removePlaceholders(normalizedData);

  const $ = cheerio.load(html);

  if (formSettings) {
    convertPointLengths($);
    mapFormFontStyles($);
    applyCourierVariants($);
    giveParagraphsTheirSize($);
    dropInlineOpacity($);
  } else {
    sanitizeInlineFontStyles($);
  }
  // Reset regex lastIndex before test for consistent behavior
  SPECIAL_CHARS_REGEX.lastIndex = 0;
  if (SPECIAL_CHARS_REGEX.test(html)) {
    wrapSpecialCharacters($);
  }
  if (html.includes("table")) {
    adjustTables($, options);
  }

  if (html.includes(BOX_ATTRIBUTE)) {
    adjustBoxes($);
  }

  if (html.includes("img")) {
    adjustImages(
      $,
      formSettings ? formImageLimits(formSettings) : LEGACY_IMAGE_LIMITS
    );
  }

  if (html.includes("tox-checklist")) {
    fixCheckboxes($);
  }

  if (formSettings && html.includes(BLANK_FIELD_ATTRIBUTE)) {
    printBlankFields($);
  }

  if (formSettings && CHECKBOX_GLYPH_REGEX.test(html)) {
    printCheckboxGlyphs($);
  }

  if (html.includes("ul") || html.includes("ol")) {
    adjustLists($);
  }

  if (options.preserveBlankLines) {
    preserveBlankLines($);
  }

  const result = $.html();

  if (!cacheable) {
    return result;
  }

  // Cache the result with size limit
  if (normalizeCache.size >= MAX_CACHE_SIZE) {
    // Remove oldest entry (simple FIFO)
    const firstKey = normalizeCache.keys().next().value;
    if (firstKey !== undefined) {
      normalizeCache.delete(firstKey);
    }
  }
  normalizeCache.set(cacheKey, result);

  return result;
};

// SN-19265: a TinyMCE blank line is `<p>&nbsp;</p>`, which survives to the browser
// preview but not to the PDF — react-pdf-html rewrites the `&nbsp;` entity to an ordinary
// space (render.js `convertEntities`) and then trims whitespace-only text nodes away
// (`bucketElements`), leaving a `<p>` with margins but no line box. Swapping the content
// for a `<br>` restores the line: react-pdf-html renders `<br>` as `<Text>{"\n"}</Text>`,
// a real line box on the library's own supported path, with no reliance on the embedded
// font carrying a glyph for an invisible character.
const BLANK_LINE_BLOCK_SELECTOR = "p, div";

// Carries no text but is still content — replacing one of these with a blank line would
// drop it out of a clinical document.
const NON_TEXT_CONTENT_SELECTOR = "img, svg, table, hr, input, video, iframe";

// A block holding these is a wrapper around other lines, not a line itself. Report bodies
// nest several levels deep, and rewriting a wrapper whose descendants are all blank would
// collapse a whole run of blank lines into a single one.
const BLOCK_CHILD_SELECTOR =
  "p, div, table, ul, ol, li, h1, h2, h3, h4, h5, h6, blockquote";

/**
 * True when `$el` is a block the author intended as a blank line — it occupies a line in
 * the editor but carries no content a reader would miss.
 *
 * Matches `<p>&nbsp;</p>`, `<p></p>`, `<div>&nbsp;</div>` and the styled-but-empty
 * `<div><span style="...">&nbsp;</span></div>` TinyMCE emits when formatting is applied to
 * an empty line. `<p><br></p>` matches too, where the rewrite is a no-op.
 */
const isBlankLineBlock = ($el: cheerio.Cheerio): boolean =>
  $el
    .text()
    .replace(/\u00a0/g, " ")
    .trim() === "" &&
  $el.find(NON_TEXT_CONTENT_SELECTOR).length === 0 &&
  $el.children(BLOCK_CHILD_SELECTOR).length === 0;

const preserveBlankLines = ($: cheerio.Root) => {
  $(BLANK_LINE_BLOCK_SELECTOR).each((_, el) => {
    const $el = $(el);
    if (isBlankLineBlock($el)) {
      $el.html("<br />");
    }
  });
};

const wrapSpecialCharacters = ($: cheerio.Root) => {
  $("body, body *")
    .contents()
    .each((_, el) => {
      if (el.type !== "text") return;

      const node = $(el);
      // Skip protected parent elements
      if (
        node.parents("script, style, textarea, pre, code").length > 0 ||
        node.parents('span[style*="font-family: Tinos"]').length > 0
      ) {
        return;
      }

      const text = el.data || "";
      // Reset regex lastIndex before test for consistent behavior
      SPECIAL_CHARS_REGEX.lastIndex = 0;
      if (!SPECIAL_CHARS_REGEX.test(text)) return;

      // Reset regex lastIndex before replace for consistent behavior
      SPECIAL_CHARS_REGEX.lastIndex = 0;
      const wrappedText = text.replace(
        SPECIAL_CHARS_REGEX,
        (match) => `<span style="font-family: Tinos">${match}</span>`
      );

      node.replaceWith(wrappedText);
    });
};

const NON_NUMERIC_FONT_SIZES = new Set([
  "xx-small",
  "x-small",
  "small",
  "medium",
  "large",
  "x-large",
  "xx-large",
  "xxx-large"
]);

const FONT_SIZE_REGEX = /font-size\s*:\s*([^;]+)/i;
const FONT_WEIGHT_REGEX = /font-weight\s*:\s*([^;]+)/i;

// Weights we actually have a Lato .ttf for (see the Font.register calls in
// pdf-document.component.tsx: LatoThin/LatoRegular/LatoSemiBold/LatoBold/LatoExtraBold).
// A value outside this set either crashes @react-pdf/font outright (e.g. "inherit",
// which isn't a resolvable CSS font-weight keyword at all) or silently falls back to
// the nearest registered file (e.g. "medium"/500) — neither prints the requested weight,
// so both cases are stripped here rather than shipping an unintended substitution.
const REGISTERED_FONT_WEIGHTS = new Set([100, 400, 600, 700, 800]);

// Only the CSS font-weight keywords that resolve to one of REGISTERED_FONT_WEIGHTS.
const FONT_WEIGHT_KEYWORD_TO_REGISTERED_WEIGHT: Record<string, number> = {
  thin: 100,
  hairline: 100,
  normal: 400,
  semibold: 600,
  demibold: 600,
  bold: 700,
  ultrabold: 800,
  extrabold: 800
};

const NUMERIC_VALUE_REGEX = /^\d+(\.\d+)?$/;

const isPrintableFontWeight = (value: string): boolean => {
  const trimmed = value.trim().toLowerCase();
  if (NUMERIC_VALUE_REGEX.test(trimmed)) {
    return REGISTERED_FONT_WEIGHTS.has(Number(trimmed));
  }
  return trimmed in FONT_WEIGHT_KEYWORD_TO_REGISTERED_WEIGHT;
};

// ENG-2277: a Form page marks its text for browser-style line breaking through the opacity its body
// hands down (FORM_TEXT_MARK), and an element with an opacity of its own would not take it. A Form's
// styles have no opacity (the editor's sanitizer drops it), so one that reached a Form's HTML another
// way is dropped here. Only a Form's HTML: every other document keeps its inline styles as they are.
const OPACITY_DECLARATION = /^\s*opacity\s*:/i;
const dropInlineOpacity = ($: cheerio.Root) => {
  $('[style*="opacity"]').each((_, element) => {
    const styles = $(element).attr("style") ?? "";
    const kept = styles
      .split(";")
      .filter((declaration) => !OPACITY_DECLARATION.test(declaration))
      .join(";");
    if (kept !== styles) $(element).attr("style", kept);
  });
};

const sanitizeInlineFontStyles = ($: cheerio.Root) => {
  $(
    '[style*="font-family"], [style*="font-size"], [style*="font-style"], [style*="font-weight"]'
  ).each((_, element) => {
    const currentStyles = $(element).attr("style") ?? "";
    if (!currentStyles) return;

    const newStyles = currentStyles
      .split(";")
      .filter((style) => {
        const trimmedStyle = style.trim();
        if (!trimmedStyle) return false;
        if (trimmedStyle.includes("font-family")) {
          return false;
        }
        if (trimmedStyle.includes("font-style")) {
          return false;
        }
        if (trimmedStyle.includes("font-size")) {
          FONT_SIZE_REGEX.lastIndex = 0;
          const fontSizeMatch = FONT_SIZE_REGEX.exec(trimmedStyle);
          if (fontSizeMatch) {
            const fontSizeValue = fontSizeMatch[1].trim().toLowerCase();
            if (NON_NUMERIC_FONT_SIZES.has(fontSizeValue)) {
              return false;
            }
          }
        }
        if (trimmedStyle.includes("font-weight")) {
          FONT_WEIGHT_REGEX.lastIndex = 0;
          const fontWeightMatch = FONT_WEIGHT_REGEX.exec(trimmedStyle);
          if (fontWeightMatch && !isPrintableFontWeight(fontWeightMatch[1])) {
            return false;
          }
        }
        return true;
      })
      .join(";");

    if (newStyles !== currentStyles) {
      $(element).attr("style", newStyles);
    }
  });
};

const deriveColumnWidthsFromColgroup = (
  $table: cheerio.Cheerio,
  $: cheerio.Root
): string[] => {
  const colgroup = $table.children("colgroup");
  const cols = colgroup.children("col");
  if (!cols.length) return [];

  const rawWidths: Array<{ value: number; unit: "%" | "px" }> = [];
  let hasPx = false;
  let hasPercent = false;

  cols.each((_, col) => {
    const styleAttr = $(col).attr("style") ?? "";
    const widthAttr = $(col).attr("width") ?? "";
    const match =
      styleAttr.match(/width\s*:\s*([\d.]+)(px|%)/i) ??
      widthAttr.match(/^([\d.]+)(px|%)?$/);

    if (!match) {
      rawWidths.push({ value: 0, unit: "%" });
      return;
    }
    const unit: "%" | "px" =
      (match[2] ?? "px").toLowerCase() === "px" ? "px" : "%";
    rawWidths.push({ value: parseFloat(match[1]), unit });
    if (unit === "px") hasPx = true;
    else hasPercent = true;
  });

  // Mixed px+% is malformed — skip and let cells auto-size
  if (hasPx && hasPercent) return [];

  if (!hasPx) {
    return rawWidths.map((w) => (w.value ? `${w.value}%` : ""));
  }

  // All px — normalize to proportional percentages
  const total = rawWidths.reduce((sum, w) => sum + w.value, 0);
  if (total === 0) return [];
  return rawWidths.map((w) => `${((w.value / total) * 100).toFixed(4)}%`);
};

// Fallback for tables without <colgroup>: reads width="X%" HTML attributes off
// first-row cells and distributes the remainder equally to unsized columns.
// Browsers handle this distribution automatically; react-pdf-html does not.
const deriveColumnWidthsFromFirstRow = (
  $table: cheerio.Cheerio,
  $: cheerio.Root
): string[] => {
  const firstRow = $table.find("tr").first();
  const cells = firstRow.children("td, th");
  if (!cells.length) return [];

  const rawWidths: (number | null)[] = [];
  let totalSpecified = 0;
  let nullCount = 0;

  cells.each((_, cell) => {
    const colspan = Math.max(
      1,
      parseInt($(cell).attr("colspan") ?? "1", 10) || 1
    );
    const pctMatch = ($(cell).attr("width") ?? "").match(/^([\d.]+)%$/);
    if (pctMatch) {
      const perCol = parseFloat(pctMatch[1]) / colspan;
      for (let c = 0; c < colspan; c++) rawWidths.push(perCol);
      totalSpecified += parseFloat(pctMatch[1]);
    } else {
      for (let c = 0; c < colspan; c++) {
        rawWidths.push(null);
        nullCount++;
      }
    }
  });

  if (nullCount === rawWidths.length) return [];

  const remaining = Math.max(0, 100 - totalSpecified);
  const autoWidth = nullCount > 0 ? remaining / nullCount : 0;

  return rawWidths.map((w) =>
    w !== null ? `${w.toFixed(5)}%` : `${autoWidth.toFixed(5)}%`
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type CellColumnInfo = Map<any, { actualCol: number; colspan: number }>;

// Pre-computes each cell's actual column position accounting for rowspan/colspan.
// Must be built before replaceWith() destroys the DOM nodes in the cell loop.
const buildCellColumnMap = ($: cheerio.Root): CellColumnInfo => {
  const map: CellColumnInfo = new Map();

  $("table").each((_, table) => {
    // Track which column indices are blocked at each row by a prior rowspan
    const spanGrid = new Map<number, Set<number>>();

    // Direct children only — avoids descending into nested tables
    $(table)
      .children("tbody, thead, tfoot")
      .children("tr")
      .each((rowIdx, row) => {
        const occupied = spanGrid.get(rowIdx) ?? new Set<number>();
        let colCursor = 0;

        $(row)
          .children("td, th")
          .each((_, cell) => {
            while (occupied.has(colCursor)) colCursor++;

            const actualCol = colCursor;
            const colspan = Math.max(
              1,
              parseInt($(cell).attr("colspan") ?? "1", 10) || 1
            );
            const rowspan = Math.max(
              1,
              parseInt($(cell).attr("rowspan") ?? "1", 10) || 1
            );

            map.set(cell, { actualCol, colspan });

            for (let r = rowIdx + 1; r < rowIdx + rowspan; r++) {
              const rowSet = spanGrid.get(r) ?? new Set<number>();
              spanGrid.set(r, rowSet);
              for (let c = actualCol; c < actualCol + colspan; c++) {
                rowSet.add(c);
              }
            }

            colCursor += colspan;
          });
      });
  });

  return map;
};

type ActiveRowspan = {
  colspan: number;
  remainingRows: number;
  sourceStyle: string;
  tagName: string;
};

const materializeRowspanSegments = ($: cheerio.Root) => {
  $("table").each((_, table) => {
    const activeSpans = new Map<number, ActiveRowspan>();

    $(table)
      .children("tbody, thead, tfoot")
      .children("tr")
      .each((_, row) => {
        const usedSpans = new Set<ActiveRowspan>();
        let colCursor = 0;

        const getSpanAt = (col: number) => activeSpans.get(col);
        const isRangeFree = (start: number, colspan: number) => {
          for (let c = start; c < start + colspan; c++) {
            if (activeSpans.has(c)) return false;
          }
          return true;
        };
        const insertContinuation = (
          insert: (html: string) => cheerio.Cheerio,
          span: ActiveRowspan
        ) => {
          const segment = span.remainingRows === 1 ? "end" : "middle";
          const colspanAttr =
            span.colspan > 1 ? ` colspan="${span.colspan}"` : "";
          insert(
            `<${span.tagName} data-rowspan-placeholder="true" data-rowspan-segment="${segment}" style="${span.sourceStyle}"${colspanAttr}>&nbsp;</${span.tagName}>`
          );
          usedSpans.add(span);
          colCursor += span.colspan;
        };

        $(row)
          .children("td, th")
          .each((_, cell) => {
            const colspan = Math.max(
              1,
              parseInt($(cell).attr("colspan") ?? "1", 10) || 1
            );

            while (!isRangeFree(colCursor, colspan)) {
              const span = getSpanAt(colCursor);
              if (span) {
                insertContinuation((html) => $(cell).before(html), span);
              } else {
                $(cell).before(
                  '<td data-rowspan-placeholder="true" data-rowspan-segment="gap">&nbsp;</td>'
                );
                colCursor++;
              }
            }

            const actualCol = colCursor;
            const rowspan = Math.max(
              1,
              parseInt($(cell).attr("rowspan") ?? "1", 10) || 1
            );

            if (rowspan > 1) {
              $(cell).attr("data-materialized-rowspan", "true");
              $(cell).attr("data-rowspan-segment", "start");
              $(cell).removeAttr("rowspan");

              const span: ActiveRowspan = {
                colspan,
                remainingRows: rowspan - 1,
                sourceStyle: $(cell).attr("style") ?? "",
                tagName: $(cell).get(0).tagName
              };
              for (let c = actualCol; c < actualCol + colspan; c++) {
                activeSpans.set(c, span);
              }
            }

            colCursor += colspan;
          });

        const seenSpans = new Set<ActiveRowspan>();
        const spansAfterCursor = [...activeSpans.entries()]
          .filter(([col, span]) => col >= colCursor && !usedSpans.has(span))
          .sort(([a], [b]) => a - b)
          .map(([, span]) => span)
          .filter((span) => {
            if (seenSpans.has(span)) return false;
            seenSpans.add(span);
            return true;
          });
        for (const span of spansAfterCursor) {
          insertContinuation((html) => $(row).append(html), span);
        }

        usedSpans.forEach((span) => {
          span.remainingRows -= 1;
          if (span.remainingRows <= 0) {
            [...activeSpans.entries()].forEach(([col, activeSpan]) => {
              if (activeSpan === span) activeSpans.delete(col);
            });
          }
        });
      });
  });
};

type TableLinesCellPosition = CellPosition & { mode: TableLinesMode };

// Grid position of every cell in a table that opts into per-table lines. Rows belong to their own table
// only (a nested table is measured separately), and rowspans occupy the slots below them so later cells
// land in the right column.
const buildTableLinesCellMap = ($: cheerio.Root) => {
  const positions = new Map<cheerio.Element, TableLinesCellPosition>();
  $(`table[${TABLE_LINES_ATTRIBUTE}]`).each((_, table) => {
    const mode = readTableLinesMode($(table).attr(TABLE_LINES_ATTRIBUTE));
    if (!mode) return;

    const rows = $(table)
      .find("tr")
      .filter((__, row) => $(row).closest("table").get(0) === table)
      .toArray();
    const occupied: Set<number>[] = rows.map(() => new Set<number>());
    const cells: {
      element: cheerio.Element;
      row: number;
      col: number;
      rowSpan: number;
      colSpan: number;
    }[] = [];
    let colCount = 0;

    rows.forEach((row, rowIndex) => {
      let col = 0;
      $(row)
        .children("td, th")
        .each((__, cell) => {
          while (occupied[rowIndex].has(col)) col++;
          const colSpan = Math.max(
            1,
            parseInt($(cell).attr("colspan") ?? "1", 10) || 1
          );
          const rowSpan = Math.max(
            1,
            parseInt($(cell).attr("rowspan") ?? "1", 10) || 1
          );
          for (
            let r = rowIndex;
            r < Math.min(rows.length, rowIndex + rowSpan);
            r++
          ) {
            for (let c = col; c < col + colSpan; c++) occupied[r].add(c);
          }
          cells.push({ element: cell, row: rowIndex, col, rowSpan, colSpan });
          col += colSpan;
          colCount = Math.max(colCount, col);
        });
    });

    cells.forEach(({ element, ...position }) => {
      positions.set(element, {
        ...position,
        rowCount: rows.length,
        colCount,
        mode
      });
    });
  });
  return positions;
};

const adjustTables = (
  $: cheerio.Root,
  options: NormalizePrintableHtmlOptions
) => {
  const enableTableLayoutFixes = Boolean(options.enableTableLayoutFixes);
  // ENG-2277: a Form's cells keep their own padding and get no spacer divs.
  const formMode = Boolean(options.formSettings);

  // Build column widths and cell positions before the cell loop —
  // replaceWith() destroys DOM nodes, so data must be captured first.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tableColumnWidths = new Map<any, string[]>();
  if (enableTableLayoutFixes) {
    $("table").each((_, table) => {
      const colgroupWidths = deriveColumnWidthsFromColgroup($(table), $);
      const widths = colgroupWidths.length
        ? colgroupWidths
        : deriveColumnWidthsFromFirstRow($(table), $);
      tableColumnWidths.set(table, widths);
    });
  }

  if (enableTableLayoutFixes && options.materializeRowspans) {
    materializeRowspanSegments($);
  }

  const cellColumnMap = enableTableLayoutFixes
    ? buildCellColumnMap($)
    : new Map();

  // Built after rowspan materialization so placeholder cells get a grid position too.
  const tableLinesCells = buildTableLinesCellMap($);

  $("table").each((_, table) => {
    if (!enableTableLayoutFixes) {
      $(table).attr("style", "border-collapse: collapse;");
      return;
    }

    const existingStyle = $(table).attr("style") ?? "";
    const parts = existingStyle
      .split(";")
      .map((s) => s.trim())
      .filter(Boolean);
    if (!parts.some((p) => p.includes("border-collapse"))) {
      parts.push("border-collapse: collapse");
    }
    $(table).attr("style", parts.join("; "));
  });

  $("tr").each((_, row) => {
    $(row).attr("style", "");
  });

  $("th, td").each((_, cell) => {
    const rowspanSegment = $(cell).attr("data-rowspan-segment") ?? "";
    const isRowspanPlaceholder =
      $(cell).attr("data-rowspan-placeholder") === "true";
    const isGapPlaceholder = isRowspanPlaceholder && rowspanSegment === "gap";
    const border = isGapPlaceholder ? "0" : ($(cell).attr("border") ?? "1");
    const bgColor = isRowspanPlaceholder ? "" : ($(cell).attr("bgcolor") ?? "");
    const hasRowspan =
      parseInt($(cell).attr("rowspan") ?? "1", 10) > 1 ||
      $(cell).attr("data-materialized-rowspan") === "true" ||
      (isRowspanPlaceholder && rowspanSegment !== "gap");
    let styles = ($(cell).attr("style") ?? "")
      .split(";")
      .map((style) => {
        const [rawName, ...rawValue] = style.split(":");
        const name = rawName.trim();
        const value = rawValue.join(":").trim();
        const prop = name.toLowerCase();
        if (!prop) return "";
        if (prop.includes("margin")) return "";
        if (prop.includes("padding") && !formMode) return "";
        if (
          enableTableLayoutFixes &&
          options.stripRowspanCellHeights &&
          hasRowspan &&
          (prop === "height" || prop === "min-height")
        ) {
          return "";
        }
        if (prop === "height") {
          return ["min-height", value].join(":");
        }
        return [name, value].join(":");
      })
      .filter(Boolean);

    let innerHtml = isRowspanPlaceholder ? "" : ($(cell).html() ?? "");
    let newStyles: string[] = [];

    // ENG-2277: a table with data-lines draws exactly its chosen lines. They go first so a border the
    // author wrote on the cell itself still wins.
    const linesPosition = tableLinesCells.get(cell);
    if (linesPosition) {
      styles = [
        ...cellBorderDeclarations(
          getCellBorders(linesPosition.mode, linesPosition)
        ),
        ...styles
      ];
    } else if (
      border !== "0" &&
      !styles.some((style) => style.includes("border-style"))
    ) {
      newStyles.push("border-style: solid");
      newStyles.push("border-collapse: collapse");
      if (!styles.some((style) => style.includes("border-width"))) {
        newStyles.push("border-width: 1px");
      }
      if (!styles.some((style) => style.includes("border-color"))) {
        newStyles.push("border-color: #ccc");
      }
    }

    if (isRowspanPlaceholder) {
      newStyles.push("padding-left: 0px");
      newStyles.push("padding-right: 0px");
      newStyles.push("padding-top: 0px");
      newStyles.push("padding-bottom: 0px");
      if (rowspanSegment === "gap") {
        newStyles.push("border-width: 0px");
      } else {
        newStyles.push("border-top-width: 0px");
        if (rowspanSegment === "middle") {
          newStyles.push("border-bottom-width: 0px");
        }
      }
    } else if (formMode) {
      if (
        !styles.some((style) =>
          style.trim().toLowerCase().startsWith("padding")
        )
      ) {
        newStyles.push(...FORM_CELL_PADDING_DECLARATIONS);
      }
      if (rowspanSegment === "start") {
        newStyles.push("border-bottom-width: 0px");
      }
    } else {
      newStyles.push("padding-left: 6.5px");
      newStyles.push("padding-right: 6.5px");
      newStyles.push("padding-top: 0px");
      if (rowspanSegment === "start") {
        newStyles.push("border-bottom-width: 0px");
      }
    }

    if (bgColor) {
      newStyles.push(`background-color: ${bgColor}`);
    }
    if (!isRowspanPlaceholder && !formMode) {
      innerHtml = `<div style="height: 6px"></div><div>${innerHtml}</div><div style="height: 6px"></div>`;
    }
    newStyles = [...styles, ...newStyles].filter((style) => style !== ":");

    // Apply column widths using the virtual grid so rowspan/colspan cells get
    // correct positions. Must read before replaceWith destroys the DOM node.
    const parentTable = enableTableLayoutFixes
      ? $(cell).closest("table").get(0)
      : undefined;
    const colWidths = parentTable
      ? (tableColumnWidths.get(parentTable) ?? [])
      : [];
    const cellInfo = enableTableLayoutFixes
      ? cellColumnMap.get(cell)
      : undefined;
    if (cellInfo && colWidths.length > 0) {
      const { actualCol, colspan } = cellInfo;
      let totalPct = 0;
      let allPresent = true;
      for (let c = actualCol; c < actualCol + colspan; c++) {
        const w = colWidths[c];
        if (!w) {
          allPresent = false;
          break;
        }
        totalPct += parseFloat(w);
      }
      if (allPresent) {
        newStyles = newStyles.filter(
          (s) => s.split(":")[0].trim().toLowerCase() !== "width"
        );
        newStyles.push(`width: ${totalPct.toFixed(5)}%`);
      }
    }

    const tagName = $(cell).get(0).tagName;

    // Preserve important table cell attributes
    const preservedAttrs = ["colspan", "rowspan", "scope", "headers"];
    let attributesString = "";
    preservedAttrs.forEach((attr) => {
      const value = $(cell).attr(attr);
      if (value) {
        attributesString += ` ${attr}="${value}"`;
      }
    });

    $(cell).replaceWith(
      `<${tagName} style="${newStyles.join(";")}"${attributesString}>${innerHtml}</${tagName}>`
    );
  });
};

type ImageAlignment = "center" | "flex-end" | "flex-start";

const TEXT_ALIGN_TO_FLEX = new Map<string, ImageAlignment>([
  ["center", "center"],
  ["right", "flex-end"],
  ["end", "flex-end"],
  ["left", "flex-start"],
  ["start", "flex-start"],
  ["justify", "flex-start"]
]);

// An image is `display: inline` unless it says otherwise, and only a block-level box
// responds to auto margins. Listing the inline values keeps every other value —
// `block`, `flex`, `grid`, `table`, `list-item`, `flow-root` — on the block side.
const INLINE_DISPLAY_VALUES = new Set([
  "inline",
  "inline-block",
  "inline-flex",
  "inline-grid",
  "inline-table"
]);

const parseStyleDeclarations = (style: string) => {
  const declarations = new Map<string, string>();
  style.split(";").forEach((declaration) => {
    const separator = declaration.indexOf(":");
    if (separator === -1) return;
    const name = declaration.slice(0, separator).trim().toLowerCase();
    // Pasted Word/Outlook markup routinely carries `!important`, which is not part of
    // the value — leaving it attached makes every keyword comparison below miss.
    const value = declaration
      .slice(separator + 1)
      .replace(/!\s*important\s*$/i, "")
      .trim()
      .toLowerCase();
    if (name && value) {
      declarations.set(name, value);
    }
  });
  return declarations;
};

// `margin` shorthand: 1 value = all sides, 2 = vertical horizontal,
// 3 = top horizontal bottom, 4 = top right bottom left. Longhand wins when both exist.
const getHorizontalMargins = (declarations: Map<string, string>) => {
  const shorthand = (declarations.get("margin") ?? "")
    .split(/\s+/)
    .filter(Boolean);
  return {
    left:
      declarations.get("margin-left") ??
      shorthand[3] ??
      shorthand[1] ??
      shorthand[0],
    right: declarations.get("margin-right") ?? shorthand[1] ?? shorthand[0]
  };
};

/**
 * TinyMCE writes alignment in three different shapes depending on what was selected:
 * `text-align` on the wrapping block (cursor in the paragraph), or `float` / auto
 * horizontal margins on the image itself (image selected). react-pdf honours neither of
 * the first two — `textAlign` does not position a non-Text child, and `float` is not a
 * supported style at all — so every shape is normalised onto `align-self`, which does
 * work against the enclosing block's (column-direction) flex cross axis.
 */
const resolveImageAlignment = (
  $: cheerio.Root,
  imgElement: cheerio.Element,
  imgStyle: string
): ImageAlignment | undefined => {
  const own = parseStyleDeclarations(imgStyle);

  const float = own.get("float");
  if (float === "right") return "flex-end";
  if (float === "left") return "flex-start";

  const display = own.get("display");
  if (display && !INLINE_DISPLAY_VALUES.has(display)) {
    // Auto margins are how a *block* box centres itself; on the default inline image
    // they do nothing, so they only count once the image has been made block-level.
    const margins = getHorizontalMargins(own);
    const autoLeft = margins.left === "auto";
    const autoRight = margins.right === "auto";
    if (autoLeft && autoRight) return "center";
    if (autoLeft) return "flex-end";
    if (autoRight) return "flex-start";
    // A block-level image is not moved by an ancestor's `text-align` either, so
    // honouring one would make the PDF disagree with the editor rather than match it.
    return "flex-start";
  }

  // `text-align` inherits, so the nearest ancestor setting it to a value we understand
  // wins. Anything else (`inherit`, `initial`, vendor keywords) keeps the walk going.
  const ancestors = $(imgElement).parents().toArray();
  for (const ancestor of ancestors) {
    const textAlign = parseStyleDeclarations(
      $(ancestor).attr("style") ?? ""
    ).get("text-align");
    const alignment = textAlign ? TEXT_ALIGN_TO_FLEX.get(textAlign) : undefined;
    if (alignment) return alignment;
  }

  return undefined;
};

const FORM_CELL_PADDING_DECLARATIONS = [
  `padding-top: ${FORM_CELL_PADDING_PX.vertical}px`,
  `padding-right: ${FORM_CELL_PADDING_PX.horizontal}px`,
  `padding-bottom: ${FORM_CELL_PADDING_PX.vertical}px`,
  `padding-left: ${FORM_CELL_PADDING_PX.horizontal}px`
];

const declarationName = (declaration: string) =>
  declaration.split(":")[0].trim().toLowerCase();
const declarationValue = (declaration: string) =>
  declaration.split(":").slice(1).join(":").trim();

// ENG-2277: a Form keeps the fonts it was drawn in. Where the legacy pass strips every font-family
// and font-style, this maps the family onto a registered one with the same letter widths
// (pdf-fonts.ts) and keeps italic. Sizes and weights the PDF cannot print are still dropped.
const mapFormFontStyles = ($: cheerio.Root) => {
  $(
    '[style*="font-family"], [style*="font-size"], [style*="font-style"], [style*="font-weight"]'
  ).each((_, element) => {
    const currentStyles = $(element).attr("style") ?? "";
    const newStyles = currentStyles
      .split(";")
      .map((declaration) => {
        const name = declarationName(declaration);
        const value = declarationValue(declaration);
        if (!name) return null;
        if (name === "font-family") {
          const family = mapSourceFontFamily(value);
          return family ? `font-family: ${family}` : null;
        }
        if (name === "font-style") {
          // Only normal and italic faces are registered; react-pdf throws on any other style.
          const style = value.toLowerCase();
          if (style === "italic" || style === "oblique")
            return "font-style: italic";
          return style === "normal" ? "font-style: normal" : null;
        }
        if (
          name === "font-size" &&
          NON_NUMERIC_FONT_SIZES.has(value.toLowerCase())
        ) {
          return null;
        }
        if (name === "font-weight" && !isPrintableFontWeight(value)) {
          return null;
        }
        return declaration.trim();
      })
      .filter(Boolean)
      .join("; ");

    if (newStyles !== currentStyles) {
      $(element).attr("style", newStyles);
    }
  });
};

const COURIER_VARIANTS: Record<string, string> = {
  regular: FORM_FONT_FAMILIES.mono,
  bold: `${FORM_FONT_FAMILIES.mono}-Bold`,
  italic: `${FORM_FONT_FAMILIES.mono}-Oblique`,
  boldItalic: `${FORM_FONT_FAMILIES.mono}-BoldOblique`
};
const COURIER_FAMILY_DECLARATION = `font-family: ${FORM_FONT_FAMILIES.mono}`;

const inlineDeclaration = (
  $: cheerio.Root,
  element: cheerio.Element,
  property: string
) =>
  ($(element).attr("style") ?? "")
    .split(";")
    .find((declaration) => declarationName(declaration) === property);

// The Form's body text size (DEFAULT_PDF_CSS_STYLE's body).
const FORM_BODY_FONT_SIZE = "14px";

// ENG-2277: react-pdf-html gives every <p> a base size of its own (18px), which beats inheritance,
// so a paragraph would print larger than the editor shows it. In a Form, a paragraph without a size
// of its own takes the size of the nearest block around it that has one, or the body's.
const giveParagraphsTheirSize = ($: cheerio.Root) => {
  $("p").each((_, paragraph) => {
    if (inlineDeclaration($, paragraph, "font-size")) return;
    const owner = $(paragraph)
      .parents()
      .toArray()
      .find((node) => inlineDeclaration($, node, "font-size"));
    const size = owner
      ? declarationValue(inlineDeclaration($, owner, "font-size") ?? "")
      : FORM_BODY_FONT_SIZE;
    const style = ($(paragraph).attr("style") ?? "").trim().replace(/;$/, "");
    $(paragraph).attr(
      "style",
      [style, `font-size: ${size}`].filter(Boolean).join("; ")
    );
  });
};

const isBoldWeight = (value: string) => {
  const weight = value.trim().toLowerCase();
  return weight === "bold" || weight === "bolder" || Number(weight) >= 600;
};

// The nearest ancestor-or-self that sets the property decides, as CSS inheritance does. A <b> or
// <strong> counts as setting bold, an <i> or <em> as setting italic.
const resolvesBold = ($: cheerio.Root, element: cheerio.Element): boolean => {
  for (const node of [element, ...$(element).parents().toArray()]) {
    const declaration = inlineDeclaration($, node, "font-weight");
    if (declaration) return isBoldWeight(declarationValue(declaration));
    if (node.tagName === "b" || node.tagName === "strong") return true;
  }
  return false;
};

const resolvesItalic = ($: cheerio.Root, element: cheerio.Element): boolean => {
  for (const node of [element, ...$(element).parents().toArray()]) {
    const declaration = inlineDeclaration($, node, "font-style");
    if (declaration)
      return declarationValue(declaration).toLowerCase() === "italic";
    if (node.tagName === "i" || node.tagName === "em") return true;
  }
  return false;
};

const nearestFontFamilyOwner = ($: cheerio.Root, element: cheerio.Element) =>
  [element, ...$(element).parents().toArray()].find((node) =>
    inlineDeclaration($, node, "font-family")
  );

// ENG-2277: Courier is a standard PDF font, and react-pdf picks a standard font by its exact name,
// ignoring font-weight and font-style. So each Courier element, and each element inside it that
// changes weight or style, names its own variant (Courier-Bold, Courier-Oblique, ...).
const applyCourierVariants = ($: cheerio.Root) => {
  $(`[style*="${COURIER_FAMILY_DECLARATION}"]`).each((_, root) => {
    // Only elements whose font is this root's: a nested element that names its own family, or
    // sits inside one that does, is not Courier.
    const restyled = $(root)
      .find('b, strong, i, em, [style*="font-weight"], [style*="font-style"]')
      .toArray()
      .filter((element) => nearestFontFamilyOwner($, element) === root);

    for (const element of [root, ...restyled]) {
      const bold = resolvesBold($, element);
      const italic = resolvesItalic($, element);
      const variant =
        COURIER_VARIANTS[
          bold
            ? italic
              ? "boldItalic"
              : "bold"
            : italic
              ? "italic"
              : "regular"
        ];
      const kept = ($(element).attr("style") ?? "")
        .split(";")
        .filter((declaration) => {
          const name = declarationName(declaration);
          return (
            name && !["font-family", "font-weight", "font-style"].includes(name)
          );
        })
        .map((declaration) => declaration.trim());
      $(element).attr("style", [`font-family: ${variant}`, ...kept].join("; "));
    }
  });
};

const PRINT_GLYPHS: Readonly<Record<string, string>> = {
  ...CHECKBOX_PRINT_GLYPHS,
  ...SYMBOL_PRINT_GLYPHS
};
const CHECKBOX_GLYPH_REGEX = new RegExp(
  `[${Object.keys(PRINT_GLYPHS).join("")}]`
);
const CHECKBOX_GLYPH_SPLIT_REGEX = new RegExp(
  `([${Object.keys(PRINT_GLYPHS).join("")}])`
);

const escapeHtmlText = (text: string) =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// SN-19949: a field typed into in the print dialog. react-pdf draws neither width nor a border on inline text, so a
// field prints as its typed text underlined, at the size it was shrunk to, then underscores out to the field's
// width. An empty field prints its original underscores, exactly as before there were fields. The caret anchor and
// the editor-only date tab do not print.
const printBlankFields = ($: cheerio.Root) => {
  $(`[${BLANK_FIELD_ATTRIBUTE}]`).each((_, element) => {
    const field = $(element);
    const island = field.find(`[${BLANK_TEXT_ATTRIBUTE}]`).first();
    const typed = island
      .text()
      .replace(/\u200B/g, "")
      .trim();
    const underscores = (count: string | undefined) =>
      "_".repeat(Math.max(0, Number(count) || 0));
    if (!typed) {
      field.replaceWith(underscores(field.attr(BLANK_LENGTH_ATTRIBUTE)));
      return;
    }
    const fontSize = /font-size:\s*([\d.]+px)/.exec(
      island.attr("style") ?? ""
    )?.[1];
    const style = `text-decoration: underline;${fontSize ? ` font-size: ${fontSize};` : ""}`;
    field.replaceWith(
      `<span style="${style}">${escapeHtmlText(typed)}</span>${underscores(field.attr(BLANK_PAD_ATTRIBUTE))}`
    );
  });
};

// ENG-2277: a Form's checkboxes print as inline squares, and its dingbats as their nearest glyph, in
// a font that has them.
const printCheckboxGlyphs = ($: cheerio.Root) => {
  $("body, body *")
    .contents()
    .each((_, node) => {
      if (node.type !== "text" || !CHECKBOX_GLYPH_REGEX.test(node.data ?? ""))
        return;
      if ($(node).parents("script, style, textarea").length > 0) return;

      const printed = (node.data ?? "")
        .split(CHECKBOX_GLYPH_SPLIT_REGEX)
        .map((part) =>
          PRINT_GLYPHS[part]
            ? `<span style="font-family: ${FORM_FONT_FAMILIES.sans}">${PRINT_GLYPHS[part]}</span>`
            : escapeHtmlText(part)
        )
        .join("");
      $(node).replaceWith(printed);
    });
};

// ENG-2277: a Form prints at 96 dpi, where a px length is a CSS px but a pt length is not
// (react-pdf scales both by 1/dpi). Rewriting pt as px makes the toolbar's pt font sizes print
// at the size the editor shows.
const convertPointLengths = ($: cheerio.Root) => {
  $('[style*="pt"]').each((_, element) => {
    const style = $(element).attr("style") ?? "";
    const converted = pointLengthsToPixels(style);
    if (converted !== style) {
      $(element).attr("style", converted);
    }
  });
};

// ENG-2277: a box with a usable size is drawn at exactly that size, with an optional line under it.
// Its content is fitted inside by adjustImages; an unsigned box's sign button was already removed
// by removePlaceholders, so it prints as the same empty box.
const SIZED_BOX_ATTRIBUTE = "data-box-sized";

const adjustBoxes = ($: cheerio.Root) => {
  $(`[${BOX_ATTRIBUTE}]`).each((_, box) => {
    const size = readBoxSize($(box).attr("style"));
    if (!size) return;
    const styles = [
      `width: ${size.width}in`,
      // A box saved wider than its cell (a converted form's guess) would run past the margin (SN-19938).
      "max-width: 100%",
      `height: ${size.height}in`,
      "overflow: hidden",
      "display: flex",
      "flex-direction: row",
      "align-items: flex-end"
    ];
    if ($(box).attr(BOX_LINE_ATTRIBUTE) !== "false") {
      styles.push(`border-bottom: ${BOX_LINE_STYLE}`);
    }
    $(box).attr("style", styles.join("; "));
    $(box).attr(SIZED_BOX_ATTRIBUTE, "true");
  });
};

// Fills the sized box it sits in; object-fit keeps the image's proportions inside the box. A
// signature sits on the box's line and is only ever made smaller to fit, as in the editor; anything
// else (a logo) sits at the top and is fitted to its box.
const boxedImageStyles = (isSignatureBox: boolean) => [
  "width: 100%",
  "height: 100%",
  `object-fit: ${isSignatureBox ? "scale-down" : "contain"}`,
  "object-position-x: 0",
  `object-position-y: ${isSignatureBox ? "100%" : "0"}`
];
const IMAGE_SIZE_PROPERTIES = new Set([
  "width",
  "height",
  "max-width",
  "max-height",
  "min-width",
  "min-height"
]);

const adjustBoxedImage = ($: cheerio.Root, imgElement: cheerio.Element) => {
  const ownStyles = ($(imgElement).attr("style") ?? "")
    .split(";")
    .map((style) => style.trim())
    .filter(
      (style) =>
        style &&
        !IMAGE_SIZE_PROPERTIES.has(style.split(":")[0].trim().toLowerCase())
    );
  const src = formatResourceUrlLocal($(imgElement).attr("src") ?? "", true);
  if (src) {
    $(imgElement).attr("src", src);
  }
  $(imgElement).removeAttr("width").removeAttr("height");
  const isSignatureBox =
    $(imgElement).closest(`[${BOX_ATTRIBUTE}="signature"]`).length > 0;
  $(imgElement).attr(
    "style",
    [...ownStyles, ...boxedImageStyles(isSignatureBox)].join(";")
  );
};

const adjustImages = (
  $: cheerio.Root,
  { contentWidth, maxWidth, sizedInAnyUnit, signatureScale = 1 }: ImageLimits
) => {
  $("img").each((_, imgElement) => {
    if ($(imgElement).closest(`[${SIZED_BOX_ATTRIBUTE}]`).length > 0) {
      adjustBoxedImage($, imgElement);
      return;
    }
    let styles = $(imgElement).attr("style") ?? "";
    let newStyles: string[] = [];

    // Resolved from the original style — `styles` is stripped of width/height below.
    const alignment = resolveImageAlignment($, imgElement, styles);
    // `flex-start` is already the default, so emitting it would only add noise. It is
    // still resolved above so that `float: left` and block-level images stop an
    // ancestor's `text-align` from centring them.
    const alignSelf = alignment === "flex-start" ? undefined : alignment;

    const alt = ($(imgElement).attr("alt") ?? "").toLowerCase();

    const isSignature = alt.includes("signature");

    const isMergeTag = alt.includes("mergetag");

    if (isSignature) {
      newStyles.push("object-fit: scale-down");
      newStyles.push("object-position-x: 0");
      newStyles.push("object-position-y: 0");
      if (isMergeTag) {
        newStyles.push(`width: ${350 * signatureScale}px`);
      } else {
        newStyles.push(`width: ${430 * signatureScale}px`);
      }

      newStyles.push(`max-height: ${110 * signatureScale}px`);
    }

    // Extract dimensions from inline style BEFORE stripping (TinyMCE stores user-resized values here).
    // Negative lookbehind prevents matching "max-width" or "min-width".
    const styleWidthMatch = /(?<![a-z-])width\s*:\s*([\d.]+)px/i.exec(styles);
    const styleHeightMatch = /(?<![a-z-])height\s*:\s*([\d.]+)px/i.exec(styles);
    // ENG-2277: a Form image's size is in inches (form-image.utils), so it is read in any unit; read
    // only as px it would be dropped and the image would print at its pixel size.
    const sized =
      sizedInAnyUnit && !(styleWidthMatch && styleHeightMatch)
        ? readBoxSize(styles)
        : null;
    const styleWidth = sized
      ? Math.round(sized.width * CSS_PX_PER_INCH * 100) / 100
      : styleWidthMatch
        ? parseFloat(styleWidthMatch[1])
        : 0;
    const styleHeight = sized
      ? Math.round(sized.height * CSS_PX_PER_INCH * 100) / 100
      : styleHeightMatch
        ? parseFloat(styleHeightMatch[1])
        : 0;

    // HTML attribute dimensions (original unresized image size)
    const attrWidth =
      parseFloat(($(imgElement).attr("width") ?? "").replace("px", "")) || 0;
    const attrHeight =
      parseFloat(($(imgElement).attr("height") ?? "").replace("px", "")) || 0;

    // Prefer inline style dimensions (user-resized); fall back to HTML attributes
    const effectiveWidth = styleWidth || attrWidth;
    const effectiveHeight = styleHeight || attrHeight;

    // Filter out width and height styles to avoid conflicts with what we push below
    styles = styles
      .split(";")
      .filter(
        (style) =>
          !style.trim().startsWith("width:") &&
          !style.trim().startsWith("height:")
      )
      .join(";");

    const src = formatResourceUrlLocal($(imgElement).attr("src") ?? "", true);

    if (!isSignature) {
      if (effectiveWidth) {
        if (effectiveWidth > contentWidth) {
          const ratio = contentWidth / effectiveWidth;
          newStyles.push(`width: ${contentWidth}px`);
          if (effectiveHeight) {
            newStyles.push(`height: ${Math.round(effectiveHeight * ratio)}px`);
          }
        } else {
          newStyles.push(`width: ${effectiveWidth}px`);
          if (effectiveHeight) {
            newStyles.push(`height: ${effectiveHeight}px`);
          }
        }
      } else if (!effectiveHeight && src?.includes("data:image")) {
        const size = getImageSize(src);
        if (
          size?.width &&
          size?.height &&
          size.width < maxWidth &&
          size.height < maxWidth
        ) {
          $(imgElement).attr("width", String(size.width * IMAGE_DOWN_RATIO));
          $(imgElement).attr("height", String(size.height * IMAGE_DOWN_RATIO));
          newStyles.push(`width: ${size.width * IMAGE_DOWN_RATIO}px`);
          newStyles.push(`height: ${size.height * IMAGE_DOWN_RATIO}px`);
        } else {
          newStyles.push(`max-width: ${maxWidth}px`);
        }
      } else if (!effectiveWidth) {
        newStyles.push(`max-width: ${maxWidth}px`);
      }
    }

    // An image wider than the page has nowhere to align to, and centring it would move
    // the clipping from one edge onto both (measured: an 800px image in the content box
    // lands at x=-160). The data-URI branch above can emit up to 799px, so check the
    // width actually being written rather than assuming the clamp caught it.
    const emittedWidth = /(?<![a-z-])width:\s*([\d.]+)px/.exec(
      newStyles.join(";")
    );
    const fitsOnPage =
      !emittedWidth || parseFloat(emittedWidth[1]) <= contentWidth;

    if (alignSelf && fitsOnPage) {
      newStyles.push(`align-self: ${alignSelf}`);
    }

    const inlineStyle = [styles, ...newStyles].join(";");

    if (src) {
      $(imgElement).attr("src", src);
    }
    $(imgElement).attr("style", inlineStyle);
  });
};

const fixCheckboxes = ($: cheerio.Root) => {
  // Reverse document order so nested tox-checklist uls are converted before
  // their ancestors — otherwise the ancestor <li>'s captured .html() still
  // contains the raw, unconverted nested markup (SN-18881).
  $("ul.tox-checklist")
    .toArray()
    .reverse()
    .forEach((ulElement) => {
      const newUl = $("<div></div>").css({
        display: "flex",
        flexDirection: "column",
        gap: "8.25px",
        marginTop: "4px",
        marginBottom: "10px"
      });

      $(ulElement)
        .children()
        .each((_, liElement) => {
          const isChecked = $(liElement).hasClass("tox-checklist--checked");
          const isCentered = ($(liElement).attr("style") ?? "").includes(
            "text-align: center"
          );

          const newLi = $("<div></div>").css({
            display: "flex",
            flexDirection: "row",
            gap: "7.5px",
            paddingLeft: "13.5px",
            justifyContent: isCentered ? "center" : undefined,
            justifySelf: isCentered ? "center" : undefined,
            justifyItems: isCentered ? "center" : undefined
          });

          const img = $("<img>")
            .css({
              width: "14px",
              height: "14px",
              borderRadius: "1px"
            })
            .attr(
              "src",
              isChecked ? CHECKED_IMAGE_DATA : NON_CHECKED_IMAGE_DATA
            );

          const innerHtml = $(liElement).html() ?? "";

          const content = $("<div></div>").html(innerHtml).css({
            marginTop: "-4px"
          });

          newLi.append(img).append(content);
          newUl.append(newLi);
        });

      $(ulElement).replaceWith(newUl);
    });
};

const adjustLists = ($: cheerio.Root) => {
  // Process ul and ol elements to allow page breaks
  $("ul, ol").each((_, listElement) => {
    const currentStyle = $(listElement).attr("style") ?? "";
    const styleParts = currentStyle
      .split(";")
      .filter((s) => s.trim())
      .map((s) => s.trim());

    // Remove any existing page-break-inside: avoid and margin: 0 (but preserve margin-bottom if set)
    const filteredStyles = styleParts.filter(
      (style) =>
        !style.includes("page-break-inside") &&
        !style.includes("break-inside") &&
        !style.toLowerCase().includes("page-break") &&
        style.trim() !== "margin: 0" &&
        !style.trim().startsWith("margin: 0 ")
    );

    // Ensure margin-bottom is set if not already present
    const hasMarginBottom = filteredStyles.some((style) =>
      style.includes("margin-bottom")
    );
    if (!hasMarginBottom) {
      filteredStyles.push("margin-bottom: 18px");
    }

    // Add styles to allow page breaks inside lists
    filteredStyles.push("page-break-inside: auto");
    filteredStyles.push("break-inside: auto");

    $(listElement).attr("style", filteredStyles.join("; "));

    // Also ensure parent divs allow breaking
    $(listElement)
      .parents("div")
      .each((_, parentDiv) => {
        const parentStyle = $(parentDiv).attr("style") ?? "";
        if (
          !parentStyle.includes("page-break-inside") &&
          !parentStyle.includes("break-inside")
        ) {
          const parentStyleParts = parentStyle
            .split(";")
            .filter((s) => s.trim())
            .map((s) => s.trim());
          parentStyleParts.push("page-break-inside: auto");
          parentStyleParts.push("break-inside: auto");
          $(parentDiv).attr("style", parentStyleParts.join("; "));
        }
      });
  });

  // Process li elements to allow page breaks
  $("li").each((_, liElement) => {
    const currentStyle = $(liElement).attr("style") ?? "";
    const styleParts = currentStyle
      .split(";")
      .filter((s) => s.trim())
      .map((s) => s.trim());

    // Remove any existing page-break properties
    const filteredStyles = styleParts.filter(
      (style) =>
        !style.includes("page-break-inside") &&
        !style.includes("page-break-after") &&
        !style.includes("break-inside") &&
        !style.includes("break-after") &&
        !style.toLowerCase().includes("page-break")
    );

    // Add styles to allow page breaks
    filteredStyles.push("page-break-inside: auto");
    filteredStyles.push("break-inside: auto");
    filteredStyles.push("page-break-after: auto");
    filteredStyles.push("break-after: auto");

    $(liElement).attr("style", filteredStyles.join("; "));
  });
};

const CONTAINER_TAGS = ["div", "span", "p"];
const nbspRegex = /^(&nbsp;|\s*)$/i;

function isEmptyContainer($el: any): boolean {
  return (
    Object.keys($el.attr() || {}).length === 0 && $el.children().length === 1
  );
}

const removeEmptyContainers = ($: any, position: string) => {
  let modified;
  do {
    modified = false;
    const elements = $(CONTAINER_TAGS.join(",")).get();
    if (elements.length === 0) break;

    let indexToCheck = -1,
      shouldRemove = false;

    if (position === "top") {
      // SN-19265: this was 1, which made the loop delete whatever sat in *second*
      // position until it found content — eating the clinician's interior blank lines —
      // while never trimming a genuinely leading one. 0 is what the original comment
      // ("check the first element only") always meant.
      indexToCheck = 0;
      const element = $(elements[indexToCheck]);
      const currentContent = element.html()?.trim();
      shouldRemove = currentContent ? nbspRegex.test(currentContent) : false;
    } else {
      // 'bottom'
      indexToCheck = elements.length - 1; //Check the last element only
      const element = $(elements[indexToCheck]);
      const currentContent = element.html()?.trim();
      shouldRemove = currentContent ? nbspRegex.test(currentContent) : false;
    }

    if (shouldRemove) {
      $(elements[indexToCheck]).remove();
      modified = true;
    }
  } while (modified);
};

export function cleanHtmlForPrint(html: string): string {
  const normalizedHtml = normalizeCharactersForPdf(html);
  const loadFn = cheerio.load as any;
  const $ = loadFn(normalizedHtml, null, false);

  //Remove unnecessary nested containers
  let modified;
  do {
    modified = false;
    const elements = $(CONTAINER_TAGS.join(",")).get();

    for (let i = elements.length - 1; i >= 0; i--) {
      const $element = $(elements[i]);
      const $parent = $element.parent();

      if (
        $parent.length &&
        CONTAINER_TAGS.includes($parent[0].tagName.toLowerCase()) &&
        isEmptyContainer($parent)
      ) {
        $element.insertBefore($parent);
        $parent.remove();
        modified = true;
        break;
      }
    }
  } while (modified);

  // Only leading and trailing empties are trimmed. Interior empty paragraphs are the
  // clinician's blank lines — SN-11518 wanted reports to stop starting halfway down the
  // page, not to have the author's paragraph breaks collapsed (SN-19265).
  removeEmptyContainers($, "top");
  removeEmptyContainers($, "bottom");

  return $.html();
}

export const DEFAULT_PDF_CSS_STYLE = `
body {
  margin: 0px;
  font-family: LatoRegular;
  line-height: 1.4;
  font-size: 14px;
  color: #000000;
}
 div {
  font-family: LatoRegular;
  line-height: 1.4;
  font-size: 14px;
  color: #000000;
}

p {
  font-family: LatoRegular;
  line-height: 1.4;
  font-size: 14px;
  margin-top: 10px;
  margin-bottom: 10px;
  padding: 0px;
  color: #000000;
}

ul {
  font-family: LatoRegular;
  line-height: 1.4;
  font-size: 14px;
  page-break-inside: auto !important;
  break-inside: auto !important;
  display: block;
  margin: 0;
  margin-top: 6px;
  margin-bottom: 18px;
  padding: 0;
  color: #000000;
}

li {
  font-family: LatoRegular;
  line-height: 1.4;
  font-size: 14px;
  margin-right: 33px;

  page-break-inside: auto !important;
  break-inside: auto !important;
  page-break-after: auto !important;
  break-after: auto !important;
  orphans: 1;
  widows: 1;
  display: list-item;
  color: #000000;
}

ol {
  font-family: LatoRegular;
  line-height: 1.4;
  font-size: 14px;
  page-break-inside: auto !important;
  break-inside: auto !important;
  display: block;
  margin: 0;
  margin-top: 6px;
  margin-bottom: 18px;
  padding: 0;
  color: #000000;
}

table {
  border-collapse: collapse;
  border-spacing: 0;
  color: #000000;
}

h1 {
  font-size: 25px;
  margin-top: 12px;
  margin-bottom: 9px;
  color: #000000;
}

h2 {
  font-size: 20px;
  margin-top: 12px;
  margin-bottom: 9px;
  color: #000000;
}

h3 {
  font-size: 16px;
  margin-top: 12px;
  margin-bottom: 9px;
  color: #000000;
}

h4 {
  font-size: 14px;
  margin-top: 12px;
  margin-bottom: 9px;
  color: #000000;
}

h5 {
  font-size: 12px;
  margin-top: 12px;
  margin-bottom: 9px;
  color: #000000;
}

h6 {
  font-size: 10px;
  margin-top: 12px;
  margin-bottom: 9px;
  color: #000000;
}

figure {
  display: table;
  margin: 16px auto;
}
figure figcaption {
  color: #999;
  display: block;
  margin-top: 4px;
  text-align: center;
}
hr {
  border-color: #333333;
  border-style: solid;
  border-width: 1px 0 0 0;
}
code {
  background-color: #e8e8e8;
  border-radius: 3px;
  padding: 1.6px 3.2px;
}
`;

// ENG-2277: a Form's text inherits its font, size, line height and colour from the element that
// sets them, as it does in the editor. The legacy sheet repeats those on div, p, ul, li and ol,
// which beats inheritance, so the Form sheet keeps them on body only.
const INHERITED_DEFAULTS =
  /\n\s*(?:font-family|font-size|line-height|color):[^;]*;/g;
// Two more differences, so a Form prints with the spacing the editor shows: single line spacing,
// and no bottom margin on a paragraph. The editor collapses one paragraph's bottom margin into the
// next one's top margin (one 10px gap); react-pdf does not collapse margins, so it would print both.
export const FORM_PDF_CSS_STYLE = DEFAULT_PDF_CSS_STYLE.replace(
  /(^|\n)(\s*(?:div|p|ul|li|ol) \{)([^}]*)\}/g,
  (_, start: string, selector: string, body: string) =>
    `${start}${selector}${body.replace(INHERITED_DEFAULTS, "")}}`
)
  .replace(
    /(^|\n)(body \{[^}]*?)line-height: [^;]*;/,
    `$1$2line-height: ${FORM_LINE_HEIGHT};`
  )
  .replace(/(\np \{[^}]*?)margin-bottom: 10px;/, "$1margin-bottom: 0px;");
